import type { StaticEntityType } from "@config";
import { nanoid } from "@reduxjs/toolkit";

const updateEntityIds = (
  entity: StaticEntityType,
  id: string,
): StaticEntityType => ({
  ...entity,
  id,
  children:
    entity.children?.map((child) => updateEntityIds(child, nanoid(20))) || [],
});

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const cloneEntity = (
  entity: StaticEntityType,
  data: Record<string, string>,
  listName?: string,
): StaticEntityType => {
  const escapedData = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      typeof value === "string" ? value.replace(/"/g, '\\"') : value,
    ]),
  );

  const replacedString = Object.entries(escapedData).reduce(
    (str, [key, value]) => {
      const jsonSafeValue = JSON.stringify(String(value)).slice(1, -1);
      let next = str.replace(new RegExp(`\\[${key}\\]`, "g"), jsonSafeValue);
      // Namespaced tokens: a variable dropped inside a list-bound container is
      // inserted as `{{<listName>.<field>}}` (see
      // features/dynamic-variables/insert/list-namespace.ts) so it can name the
      // row field it refers to. Substitute those per cloned row alongside the
      // legacy `[field]` placeholders. Triple braces (raw-HTML variables) first,
      // so the double-brace pattern can't eat their inner braces.
      if (listName) {
        const scoped = `${escapeRegExp(listName)}\\.${escapeRegExp(key)}`;
        next = next
          .replace(
            new RegExp(`\\{\\{\\{\\s*${scoped}\\s*\\}\\}\\}`, "g"),
            jsonSafeValue,
          )
          .replace(
            new RegExp(`\\{\\{\\s*${scoped}\\s*\\}\\}`, "g"),
            jsonSafeValue,
          );
      }
      return next;
    },
    JSON.stringify(entity),
  );
  return updateEntityIds(JSON.parse(replacedString), nanoid(20));
};

/**
 * When an entity is expanded per item of a list dynamic variable (a slide or a
 * combo such as Tabs), any chart / table / combo nested inside references its
 * own list variable by name (e.g. "Views_per_listing"). That data lives inside
 * the current item, not at the root of the version data, so the root lookup in
 * `compileStory` misses it.
 *
 * Walk the cloned subtree and stash each matching nested value on the entity's
 * settings as `listDynamicVariableData` so `compileStory` can resolve it from
 * the item scope instead of the (absent) root entry.
 */
const injectNestedListVariableData = (
  entity: StaticEntityType,
  item: Record<string, unknown>,
): void => {
  const key = entity.settings?.listDynamicVariable as string | undefined;
  if (key && item[key] !== undefined) {
    entity.settings.listDynamicVariableData = item[key];
  }
  entity.children?.forEach((child) =>
    injectNestedListVariableData(child, item),
  );
};

export const parseListDynamicVariable = (
  children: StaticEntityType["children"],
  data: Record<string, string>[],
): StaticEntityType["children"] => {
  if (!data?.length) return children;

  const child = children[0];
  return data.map((item) => {
    const cloned = cloneEntity(child, item);
    injectNestedListVariableData(cloned, item);
    return cloned;
  });
};

export const parseListDynamicVariableForSlides = (
  slide: StaticEntityType,
  data: Record<string, string>[],
  listName?: string,
): StaticEntityType[] => {
  if (!data?.length) return [slide];

  const template = JSON.parse(JSON.stringify(slide));

  return data.map((item) => {
    const parsed = cloneEntity(template, item, listName);
    injectNestedListVariableData(parsed, item);
    return {
      ...parsed,
      id: nanoid(20),
    };
  });
};
